import Head from "next/head"
import { useState } from "react"
import { t } from "translations"

import Accordion from "@mui/material/Accordion"
import AccordionDetails from "@mui/material/AccordionDetails"
import AccordionSummary from "@mui/material/AccordionSummary"
import Autocomplete, { AutocompleteProps } from "@mui/material/Autocomplete"
import Skeleton from "@mui/material/Skeleton"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"

import { getFooter, getPage } from "data/contentData/api/content"
import { getMegamenu } from "data/contentData/api/megamenu"
import { IMegamenuCategory } from "data/contentData/interfaces/IMegamenuCategory"
import { IAuthor } from "data/contentData/interfaces/mongodb/IAuthor"
import { IContainerCard } from "data/contentData/interfaces/mongodb/IContainerCard"
import { IPage } from "data/contentData/interfaces/mongodb/IPage"
import { IPublic } from "data/contentData/interfaces/mongodb/IPublic"
import useQueryData from "data/hooks/useQueryData"
import { IDeviceDimensions } from "lib/hooks/deviceDimensions/IDeviceDimensions"
import useDeviceDimensions from "lib/hooks/deviceDimensions/useDeviceDimensions"
import useGtmCustomVariables from "lib/hooks/useGtmCustomVariables"
import { staticPaths } from "lib/routes"

import ArticleMeta from "Components/Article/ArticleMeta"
import Icon from "Components/Icon/Icon"
import Layout from "Components/Layout/Layout"
import PortableText from "Components/PortableText/PortableText"

const bodyDeviceDimensions: IDeviceDimensions = {
    sm: {
        height: 1800
    },
    md: {
        height: 950
    },
    lg: {
        height: 950
    }
}

export interface ICountry {
    url: string
    name: string
    continent: string
}

export default function TravelVaccinations({
    countries,
    page: initialPage,
    footer,
    megamenu
}: {
    countries: ICountry[]
    page: IPublic
    footer: IContainerCard[]
    megamenu: IMegamenuCategory[]
}) {
    const breakpoint = useDeviceDimensions()
    useGtmCustomVariables()

    const bodySkeletonValues = bodyDeviceDimensions[breakpoint]

    const [selectedCountry, setSelectedCountry] = useState<string | null>(null)

    const handleCountryChange: AutocompleteProps<
        ICountry,
        undefined,
        undefined,
        undefined
    >["onChange"] = (_, value) => {
        setSelectedCountry(value?.url ?? "")
        scrollTo(0, 0)
    }

    const {
        data: countryPage,
        error,
        isLoading
    } = useQueryData<IPage | null | IPublic>(
        () =>
            selectedCountry ? `travel-vaccionations-${selectedCountry}` : null,
        () => getPage(selectedCountry as string, true)
    )

    if (error) return null

    const page = (countryPage as IPublic) ?? initialPage

    return (
        <Layout
            footer={footer}
            megamenu={megamenu}>
            <Head>
                <title>{t.travelVaccinations.title}</title>
                <meta
                    name={t.travelVaccinations.title}
                    content={t.travelVaccinations.title}
                    key="desc"
                />
            </Head>
            <div style={{ display: "relative" }}>
                <StyledStickyHeader>
                    <Typography
                        color={"white"}
                        variant="h1"
                        textAlign={"center"}>
                        {initialPage.title}
                    </Typography>

                    <StyledSelectCountry
                        options={countries}
                        onChange={handleCountryChange}
                    />
                </StyledStickyHeader>

                <StyledMain className="article">
                    {isLoading ? (
                        <Skeleton height={bodySkeletonValues.height} />
                    ) : (
                        <div>
                            {selectedCountry && (
                                <>
                                    <Typography
                                        variant="h1"
                                        component="h2"
                                        marginBottom={2}>
                                        {page.title}
                                    </Typography>
                                    <ArticleMeta page={page as IPublic} />
                                </>
                            )}

                            {page.sections?.map(currentSection => (
                                <StyledImageSection key={currentSection.url}>
                                    <Typography variant="h3">
                                        {currentSection.name}
                                    </Typography>
                                    <PortableText
                                        value={currentSection.blockContent}
                                    />
                                    {currentSection.sections?.map(
                                        subSection => (
                                            <section key={subSection.url}>
                                                <Typography variant="h4">
                                                    {subSection.name}
                                                </Typography>
                                                <PortableText
                                                    value={
                                                        subSection.blockContent
                                                    }
                                                />
                                            </section>
                                        )
                                    )}
                                </StyledImageSection>
                            ))}

                            {page.sources && page.sources?.sections?.length && (
                                <>
                                    <Typography
                                        variant="h3"
                                        marginBottom={2}>
                                        {page.sources.name}
                                    </Typography>
                                    {page.sources?.sections?.map(
                                        currentSection => (
                                            <Accordion
                                                key={currentSection.name}>
                                                <AccordionSummary
                                                    color="primary"
                                                    expandIcon={
                                                        <Icon name="expandMore" />
                                                    }>
                                                    <Typography variant="h4">
                                                        {currentSection.name}
                                                    </Typography>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Typography variant="body2">
                                                        <PortableText
                                                            value={
                                                                currentSection.blockContent
                                                            }
                                                        />
                                                    </Typography>
                                                </AccordionDetails>
                                            </Accordion>
                                        )
                                    )}
                                </>
                            )}

                            {page.authors && (
                                <AuthorsSection authors={page.authors} />
                            )}
                        </div>
                    )}
                </StyledMain>
            </div>
        </Layout>
    )
}

type CountrySelectProps = Omit<
    AutocompleteProps<ICountry, undefined, undefined, undefined>,
    "renderInput" | "getOptionLabel"
>

function SelectCountry(props: CountrySelectProps) {
    return (
        <Autocomplete
            autoHighlight
            getOptionLabel={country => country.name}
            groupBy={country => country.continent}
            noOptionsText={t.travelVaccinations.noCountryFound}
            renderInput={params => (
                <TextField
                    {...params}
                    label={t.travelVaccinations.chooseCountry}
                    variant="filled"
                />
            )}
            {...props}
        />
    )
}

interface IAuthorsSectionProps {
    authors: IAuthor[]
}

function AuthorsSection({ authors }: IAuthorsSectionProps) {
    const [current, former] = authors.reduce(
        (acc, author) => {
            acc[author.current ? 0 : 1].push(author)

            return acc
        },
        [[], []] as IAuthorsSectionProps["authors"][]
    )

    return (
        <Accordion>
            <AccordionSummary
                color="primary"
                expandIcon={<Icon name="expandMore" />}>
                <Typography variant="h4">
                    {t.travelVaccinations.proffesionalStaff}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <ul>
                    {current.map(author => (
                        <Typography
                            component="li"
                            key={author.name}
                            variant="body2">
                            {`${author.name}, ${author.title}`}
                        </Typography>
                    ))}
                </ul>
                {former.length > 0 && (
                    <>
                        <Typography variant="h4">
                            {t.travelVaccinations.previousProffesionalStaff}
                        </Typography>
                        <ul>
                            {former.map(author => (
                                <Typography
                                    component="li"
                                    key={author.name}
                                    variant="body2">
                                    {`${author.name}, ${author.title}`}
                                </Typography>
                            ))}
                        </ul>
                    </>
                )}
            </AccordionDetails>
        </Accordion>
    )
}

export async function getStaticProps() {
    const page = await getPage(staticPaths.travelVaccinationsSearch, true)
    const allCountries = await getPage(staticPaths.allCountries, true)

    const countries = allCountries?.children?.reduce(
        (acc, { children, name }) => [
            ...acc,
            ...(children?.map(country => ({ ...country, continent: name })) ??
                [])
        ],
        [] as ICountry[]
    )

    const revalidate = Number(process.env.REVALIDATE_STATIC_PROPS)
    const footer = await getFooter()
    const megamenu = await getMegamenu()

    return {
        props: {
            countries,
            page,
            footer,
            megamenu
        },
        revalidate
    }
}

const StyledMain = styled("main")(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2),
    gap: theme.spacing(4),
    [theme.breakpoints.up("md")]: {
        padding: theme.spacing(4)
    }
}))

const StyledStickyHeader = styled("div")(({ theme }) => ({
    zIndex: 1099,
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    position: "sticky",
    top: 0,
    backgroundColor: theme.palette.secondary.main
}))

const StyledSelectCountry = styled(SelectCountry)(({ theme }) => ({
    paddingTop: theme.spacing(2),
    "& .MuiAutocomplete-inputRoot": {
        backgroundColor: "white",
        "&:hover": {
            backgroundColor: "white"
        },
        "&.Mui-focused": {
            backgroundColor: "white"
        }
    },
    "& .MuiTextField-root": {
        borderRadius: theme.shape.borderRadius
    },
    "& .MuiInputBase-root &. MuiFilledInput-root": {
        "&:hover": {
            backgroundColor: "white"
        }
    }
}))

const StyledImageSection = styled("section")(({ }) => ({
    "&:has(img)": {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        "& div": { float: "none", maxWidth: "100%" }
    }
}))
